import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { navHeight } from './constants';
import { ProductDirectory } from './product-directory';
import { productPanelId } from './product-switcher-ids';
import { useProductSwitcher } from './product-switcher-context';

type BarBox = {
  left: number;
  width: number;
  bottom: number;
};

/**
 * The directory as a menu that opens from the context bar: the same width, aligned to it, over
 * a scrim that dims the page behind. It stays a region rather than a dialog, so it reads as
 * part of the page; clip-path unrolls it downward without distorting its content.
 */
export function ProductDirectoryPanel() {
  const switcher = useProductSwitcher();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [bar, setBar] = useState<BarBox>({ left: 0, width: 0, bottom: 0 });
  const isOpen = switcher !== null && switcher.isPanelOpen;
  const closePanel = switcher?.closePanel;
  const triggerRef = switcher?.triggerRef;
  const barRef = switcher?.barRef;

  // The menu belongs to the bar, so it measures the bar rather than guessing at a width.
  useEffect(() => {
    const element = barRef?.current;
    if (!isOpen || !element) {
      return undefined;
    }
    function measure() {
      if (element) {
        const box = element.getBoundingClientRect();
        setBar({ left: box.left, width: box.width, bottom: box.bottom });
      }
    }
    measure();
    let observer: ResizeObserver | undefined = undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(element);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', measure);
    };
  }, [isOpen, barRef]);

  useEffect(() => {
    if (!isOpen || !closePanel) {
      return undefined;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && closePanel) {
        event.preventDefault();
        closePanel();
        const inPanel = panelRef.current !== null && panelRef.current.contains(document.activeElement);
        if (inPanel && triggerRef && triggerRef.current) {
          triggerRef.current.focus();
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closePanel, triggerRef]);

  if (switcher === null || switcher.product.length === 0) {
    return null;
  }

  // A member of one organization is always in it, so the switch is noise rather than a choice.
  const body = (
    <ProductDirectory
      directory={switcher.directory}
      hasFailed={switcher.hasFailed}
      favorites={switcher.favorites}
      onToggleFavorite={switcher.toggleFavorite}
      onReorderFavorites={switcher.reorderFavorites}
      onLaunch={switcher.launch}
      query={switcher.query}
      mode={switcher.mode}
      organizations={switcher.organizations}
      selectedOrganization={switcher.selectedOrganization}
      onOrganizationChange={switcher.onOrganizationChange}
      onSearchOrganizations={() => switcher.openPanel('organizations')}
    />
  );

  let transition = 'clip-path 220ms ease, opacity 220ms ease';
  let scrimTransition = 'opacity 220ms ease';
  if (reduceMotion) {
    transition = 'none';
    scrimTransition = 'none';
  }

  // Anchored to the bar's own bottom edge, so the menu unrolls out of the control rather than
  // out of the toolbar. The overlap slides under the app bar, which sits above it.
  let panelWidth: number | string = '100%';
  let panelLeft: number | string = 0;
  let panelTop = navHeight;
  if (bar.width > 0) {
    panelWidth = bar.width;
    panelLeft = bar.left;
    panelTop = bar.bottom;
  }

  return createPortal(
    <>
      <Box
        data-panel-scrim
        aria-hidden="true"
        onClick={closePanel}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.drawer - 1,
          bgcolor: 'rgba(15, 18, 26, 0.45)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: scrimTransition,
        }}
      />
      <Box
      id={productPanelId}
      ref={panelRef}
      role="region"
      tabIndex={-1}
      aria-label="Change organization or product"
      inert={!isOpen}
      aria-hidden={!isOpen}
      sx={{
        position: 'fixed',
        top: `${panelTop}px`,
        left: panelLeft,
        width: panelWidth,
        // The width measured off the bar is a border-box width, so this one must be too, or the
        // sheet carries its borders outside it and steps past the bar by a pixel on each side.
        boxSizing: 'border-box',
        maxWidth: '100vw',
        maxHeight: `calc(100dvh - ${panelTop + 16}px)`,
        // Above the app bar, so the card meets the bar's bottom edge instead of sliding under it.
        zIndex: (theme) => theme.zIndex.drawer + 2,
        bgcolor: 'var(--color-backdrop-secondary)',
        overflow: 'hidden',
        overflowY: 'auto',
        borderRadius: '0 0 16px 16px',
        border: '1px solid',
        borderColor: 'divider',
        // The bar's own bottom edge is the seam, so the sheet brings no second line to it.
        borderTopWidth: 0,
        // Cast downward only: a shadow that spreads upward smudges the join with the bar.
        boxShadow: '0 12px 28px rgba(15, 18, 26, 0.18), 0 2px 8px rgba(15, 18, 26, 0.08)',
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
        // Only the bottom edge is animated. The sides and bottom are held clear of the clip so it
        // never shaves the borders at a fractional pixel or eats the shadow, and the corners are
        // left to border-radius, which anti-aliases where a clip does not.
        clipPath: isOpen ? 'inset(0px -24px -24px -24px)' : 'inset(0px -24px 100% -24px)',
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
        transition: transition,
      }}
    >
      {body}
      </Box>
    </>,
    document.body
  );
}
